import React, { useMemo, useState, useEffect } from "react";
// import { DataContext } from '../components/DataContext';

// ---- Simple helpers for fixed "YYYY-MM-DD" strings ----

// const parseDateYMD = (raw) => {
//   if (typeof raw !== "string") return null;
//   const m = raw.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
//   if (!m) return null;
//   const y = Number(m[1]), mo = Number(m[2]) - 1, d = Number(m[3]);
//   const date = new Date(y, mo, d);
//   return isNaN(date.getTime()) ? null : date;
// };

/**
 * Reusable TopAffectedRegionsBar
 * @param {Array} data - Array of data objects
 * @param {function} valueAccessor - Function (item) => {key: string, name: string, count: number} | null
 * @param {string} title - Chart title
 * @param {boolean} loading - Loading state (optional)
 * @param {number} topN - Number of top regions to show (default 5)
 * @param {string} barColor - Tailwind color class for bars (optional)
 */
function TopAffectedRegionsBar({
  data = [],
  valueAccessor = () => null,
  title = "Top Affected Regions",
  loading = false,
  topN = 5,
  barColor = "bg-teal-600"
}) {
  // Aggregate counts by key
  const counts = useMemo(() => {
    if (!data || data.length === 0) return [];
    const tally = new Map();
    const nameMap = new Map();
    for (const item of data) {
      const v = valueAccessor(item);
      if (!v || !v.key || !v.name || typeof v.count !== "number") continue;
      tally.set(v.key, (tally.get(v.key) ?? 0) + v.count);
      if (!nameMap.has(v.key)) nameMap.set(v.key, v.name);
    }
    const rows = Array.from(tally.entries()).map(([key, count]) => ({
      key,
      name: nameMap.get(key) ?? key,
      count
    }));
    rows.sort((a, b) => b.count - a.count);
    return rows.slice(0, topN);
  }, [data, valueAccessor, topN]);

  const maxCount = counts.length ? counts[0].count : 0;

  // Animate after data loads
  const [animateOn, setAnimateOn] = useState(false);
  useEffect(() => {
    if (!loading) {
      const id = setTimeout(() => setAnimateOn(true), 60);
      return () => clearTimeout(id);
    } else {
      setAnimateOn(false);
    }
  }, [loading]);

  const rowsToShow =
    loading || counts.length === 0
      ? Array.from({ length: topN }, (_, i) => ({
          key: `skeleton-${i}`,
          name: "—",
          count: 0,
          skeleton: true
        }))
      : counts;

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-xl font-semibold mb-4">{title}</h3>

      <div className="h-64 bg-gray-100 rounded-md flex items-center justify-center">
        <div className="w-full px-4 space-y-4">
          {rowsToShow.map((row) => {
          const pct = !row.skeleton && maxCount > 0 ? (row.count / maxCount) * 100 : 0;
          return (
            <div key={row.key}>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium truncate">{row.name}</span>
                <span className="text-sm font-medium">
                  {row.skeleton ? "—" : `${row.count} occurrences`}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className={[
                    `${barColor} h-2.5 rounded-full transition-[width] duration-700 ease-out`,
                    row.skeleton ? "animate-pulse" : "",
                  ].join(" ")}
                  style={{
                    width: row.skeleton || !animateOn ? "0%" : `${Math.max(2, pct)}%`,
                  }}
                  aria-label={
                    row.skeleton
                      ? "Loading"
                      : `${row.name}: ${row.count} of ${maxCount}`
                  }
                />
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}

export default TopAffectedRegionsBar;
